import React from 'react';
import { IconButton, TextField } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ArrowLeft, ArrowRight, Clear } from '@mui/icons-material';
import { CalendarIcon } from '@mui/x-date-pickers/icons';
import { enUS as enLocale } from 'date-fns/locale/en-US';
import { hr as hrLocale } from 'date-fns/locale/hr';
import { useTranslation } from 'react-i18next';
import { addDays, addMonths, addYears, format, isAfter, isBefore, parseISO } from 'date-fns';
import { styled } from '@mui/material/styles';
import { RenderIf } from '../render-if';

const StyledDateContainer = styled('div')`
  display: flex;
  flex-direction: row;
  gap: 3px;
  align-items: center;
`;

const StyledImageButtonContainer = styled('div')`
  height: 2rem;
  margin-top: -1.5rem;
`;

export interface FormInputDatePlainProps {
  label?: string;
  variant?: 'standard' | 'outlined' | 'filled';
  dataTestId?: string;

  dateVariant?: 'date' | 'year-month' | 'year';
  onChange: (value: any) => void;
  defaultValue?: any;
  error?: any;
  hideHelperTextIfEmpty?: boolean;
  isDeactivated?: boolean;
  clearableDate?: boolean;
  minDate?: Date | null;
  maxDate?: Date | null;
}

export const FormInputDatePlain = (props: FormInputDatePlainProps) => {
  const { i18n } = useTranslation();
  const locale = i18n.language === 'hr' ? hrLocale : enLocale;
  const [value, changeValue] = React.useState(props.defaultValue);
  const [open, setOpen] = React.useState(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  // Refs for mutable values accessed by the stable slot component
  const slotDataRef = React.useRef({
    value,
    moveDate: undefined as unknown as (delta: number) => void,
    clearDate: undefined as unknown as () => void,
    dataTestId: props.dataTestId,
    clearableDate: props.clearableDate,
  });

  React.useEffect(() => {
    changeValue(props.defaultValue);
  }, [props.defaultValue]);

  // Updated to use string array for views instead of CalendarPickerView type
  let dateViews: string[] | undefined;
  if (props.dateVariant === 'year-month') {
    dateViews = ['year', 'month'];
  } else if (props.dateVariant === 'year') {
    dateViews = ['year'];
  }

  function dateToString(date: Date) {
    try {
      if (props.dateVariant === 'year-month') {
        return format(date, 'yyyy-MM');
      } else if (props.dateVariant === 'year') {
        return format(date, 'yyyy');
      }
      return format(date, 'yyyy-MM-dd');
    } catch (e) {
      return null;
    }
  }

  function getInputFormat() {
    if (props.dateVariant === 'year') {
      return 'yyyy';
    } else if (props.dateVariant !== 'year-month') {
      return i18n.language === 'hr' ? 'dd.MM.yyyy' : 'MM/dd/yyyy';
    }
    return i18n.language === 'hr' ? 'MM.yyyy' : 'MM/yyyy';
  }

  function getInputMask() {
    if (props.dateVariant === 'year') {
      return '____';
    } else if (props.dateVariant !== 'year-month') {
      return i18n.language === 'hr' ? '__.__.____' : '__/__/____';
    }
    return i18n.language === 'hr' ? '__.____' : '__/____';
  }

  function onDateChange(val: Date | string | null) {
    let stringVal: string | Date | null = val != null ? val : '';
    if (val instanceof Date) {
      stringVal = dateToString(val);
    }
    props.onChange(stringVal);
    changeValue(stringVal);
  }

  function moveDate(delta: number) {
    let date = parseISO(value);
    if (props.dateVariant === 'year-month') {
      date = addMonths(date, delta);
      if (delta > 0 && props.maxDate != null && isAfter(date, props.maxDate)) {
        date = props.maxDate;
        date.setDate(1);
      } else if (delta < 0 && props.minDate != null && isBefore(date, props.minDate)) {
        date = props.minDate;
        date.setDate(1);
      }
    } else if (props.dateVariant === 'year') {
      date = addYears(date, delta);
      if (delta > 0 && props.maxDate != null && isAfter(date, props.maxDate)) {
        date = props.maxDate;
        date.setDate(1);
      } else if (delta < 0 && props.minDate != null && isBefore(date, props.minDate)) {
        date = props.minDate;
        date.setDate(1);
      }
    } else {
      date = addDays(date, delta);
      if (delta > 0 && props.maxDate != null && isAfter(date, props.maxDate)) {
        date = props.maxDate;
      } else if (delta < 0 && props.minDate != null && isBefore(date, props.minDate)) {
        date = props.minDate;
      }
    }
    onDateChange(date);
  }

  // Convert null values to undefined for minDate/maxDate
  const minDate = props.minDate === null ? undefined : props.minDate;
  const maxDate = props.maxDate === null ? undefined : props.maxDate;

  function clearDate() {
    onDateChange(null);
  }

  // Keep ref in sync with current values for the stable slot component
  slotDataRef.current = { value, moveDate, clearDate, dataTestId: props.dataTestId, clearableDate: props.clearableDate };

  // Stable icon slot — switches between calendar and clear based on current value
  const [PickerIconSlot] = React.useState(
    () =>
      function PickerIcon(iconProps: any) {
        const { value: val, clearableDate: clr } = slotDataRef.current;
        if (val !== '' && clr !== false) {
          return <Clear {...iconProps} />;
        }
        return <CalendarIcon {...iconProps} />;
      },
  );

  // Stable textField slot component — created once so React never unmounts/remounts
  // the container, keeping the Popper anchor element stable across re-renders.
  const [TextFieldSlot] = React.useState(
    () =>
      function DateTextField(params: any) {
        const { value: val, moveDate: md, clearDate: cd, dataTestId: dtid, clearableDate: clr } = slotDataRef.current;
        return (
          <StyledDateContainer ref={containerRef}>
            <RenderIf true={val !== ''}>
              <StyledImageButtonContainer>
                <IconButton onClick={() => md(-1)} data-test-id={`${params.inputProps?.['data-test-id'] || ''}-left`}>
                  <ArrowLeft />
                </IconButton>
              </StyledImageButtonContainer>
            </RenderIf>
            <TextField {...params} />
            <RenderIf true={val === '' && dtid?.endsWith('-from')}>
              <div style={{ width: '1rem' }} />
            </RenderIf>
            <RenderIf true={val !== ''}>
              <StyledImageButtonContainer>
                <IconButton onClick={() => md(1)} data-test-id={`${params.inputProps?.['data-test-id'] || ''}-right`}>
                  <ArrowRight />
                </IconButton>
              </StyledImageButtonContainer>
            </RenderIf>
          </StyledDateContainer>
        );
      },
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={locale}>
      <DatePicker
        open={open}
        onClose={() => setOpen(false)}
        views={dateViews as any}
        format={getInputFormat()}
        value={value !== '' && typeof value === 'string' ? parseISO(value) : null}
        onChange={(val: Date | null) => onDateChange(val)}
        minDate={minDate}
        maxDate={maxDate}
        slotProps={{
          textField: {
            size: 'small',
            margin: 'normal',
            variant: props.variant,
            fullWidth: true,
            label: props.label,
            onClick: () => setOpen(true),
            sx: {
              marginTop: props.hideHelperTextIfEmpty ? '11px' : undefined,
              caretColor: 'transparent',
              cursor: 'pointer',
            },
            helperText: props.error ? props.error.message : props.hideHelperTextIfEmpty ? '' : ' ',
            error: !!props.error,
            disabled: props.isDeactivated,
            inputProps: {
              'data-test-id': props.dataTestId || `input-${props.label?.replaceAll(' ', '-')}`,
            },
          },
          openPickerButton: {
            onClick: (e: React.MouseEvent) => {
              if (value !== '' && props.clearableDate !== false) {
                e.stopPropagation();
                clearDate();
              } else {
                setOpen(true);
              }
            },
          } as any,
          popper: {
            anchorEl: () => containerRef.current!,
          },
        }}
        // The textField slot below renders a plain TextField - an <input/> - and pickers v8 assumes the
        // accessible field structure (PickersSectionList) unless told otherwise. Without this it throws
        // "The `sectionListRef` prop has not been initialized" the moment a picker is rendered.
        enableAccessibleFieldDOMStructure={false}
        slots={{
          textField: TextFieldSlot,
          openPickerIcon: PickerIconSlot,
        }}
      />
    </LocalizationProvider>
  );
};
